import { useQuery } from '@tanstack/react-query';
import { UseFormReturn, useFieldArray } from 'react-hook-form';
import { toast } from 'react-toastify';
import { z } from 'zod';

import { ActivityFormSchema } from '@/constants/formSchema/ActivityFormSchema';
import api from '@/services/apiSgft';
import { EmployeeTypeEnum } from '@/types/ActivityData';
import { Option } from '@/types/Filter';

import { Button } from '../atoms/Button';
import ControlledSimpleSelect from '../atoms/ControlledSimpleSelect';
import TrashButton from '../atoms/TrashButton';
import { ActivityPoleCondition } from './ActivityPoleCondition';

interface ActivityPoleProps {
	poleIndex: number;
	poleId: string;
	form: UseFormReturn<z.infer<typeof ActivityFormSchema>>;
	removePole: (poleIndex: number) => void;
	employeeType: EmployeeTypeEnum;
}

export const ActivityPole = ({
	poleIndex,
	poleId,
	form,
	removePole,
	employeeType,
}: ActivityPoleProps) => {
	const control = form.control;
	const baseName =
		employeeType === EmployeeTypeEnum.INTERNAL
			? 'internalData.activityPoles'
			: 'thirdPartyData.activityPoles';
	const {
		fields,
		append: appendCondition,
		remove: removeCondition,
	} = useFieldArray<z.infer<typeof ActivityFormSchema>>({
		control,
		name: `${baseName}.${poleIndex}.conditions`,
	});

	const getAllPoles = async () => {
		try {
			const response = await api.get('/pole/all', {
				params: {},
			});
			return response.data.map((el: { id: number; name: string }) => ({
				value: el.id,
				label: el.name,
			}));
		} catch (e) {
			toast.error('Erro ao carregar os Polos!', {
				theme: 'colored',
				toastId: 'error',
			});
		}
	};

	const { data: allPoles } = useQuery<Option[]>(['get-all-poles'], getAllPoles);

	const selectedPoles = form.watch(`${baseName}`).map((el) => el.poleId);
	const availablePoles = allPoles?.filter(
		(el) =>
			!selectedPoles.some(
				(selectedPole, selectedIndex) =>
					selectedPole === el.value && selectedIndex !== poleIndex,
			),
	);
	return (
		<div className="w-full rounded-md bg-gray-100 px-4 py-3">
			<div className="flex items-end justify-between">
				<ControlledSimpleSelect
					{...form.register(`${baseName}.${poleIndex}.poleId`)}
					title="Polo"
					required
					control={control}
					values={availablePoles}
					placeholder="Selecione o Polo"
					width="52rem"
				/>
				<TrashButton onClick={() => removePole(poleIndex)} color="action" />
			</div>
			<div className="flex flex-col gap-2 py-2">
				{fields.map((condition, conditionIndex) => {
					return (
						<ActivityPoleCondition
							key={`condition-${poleId}-${condition.id}`}
							poleIndex={poleIndex}
							poleId={poleId}
							conditionId={condition.id}
							form={form}
							removeCondition={removeCondition}
							employeeType={employeeType}
							conditionIndex={conditionIndex}
						/>
					);
				})}
			</div>
			<Button
				className="px-0 text-xs font-medium text-primary hover:opacity-80"
				onClick={() =>
					appendCondition({
						trainingType: null,
						trainings: [
							{
								trainingId: null,
							},
						],
						id: null,
						poleId: null,
					})
				}
				type="button"
				variant="destructive"
			>
				+ Adicionar Treinamentos
			</Button>
		</div>
	);
};
